/* Shopify validates candidates and renders native forms; this controller
 * expands the related collection search without an arbitrary first-page limit. */
(() => {
  const cache = new Map();
  class BrincatoysCartAddons extends HTMLElement {
    static sources(plan) {
      const sources = new Map();
      for (const source of plan.sources || []) {
        if (!(source.ceiling > 0) || !source.url) continue;
        const old = sources.get(source.url);
        if (old) old.ceiling = Math.max(old.ceiling, source.ceiling);
        else sources.set(source.url, { ...source });
      }
      return [...sources.values()].sort((a, b) => a.tier - b.tier || a.size - b.size || b.ceiling - a.ceiling);
    }
    static async resolve(plan, loadPage) {
      const candidates = [...new Set((plan.seedHandles || []).filter(Boolean))].slice(0, 2).map(handle => ({ handle }));
      const handles = new Set(candidates.map(candidate => candidate.handle));
      let failures = 0;
      for (const source of this.sources(plan)) {
        if (candidates.length >= 2) break;
        let next = source.url;
        const visited = new Set();
        while (next && candidates.length < 2 && !visited.has(next)) {
          visited.add(next);
          try {
            const page = await loadPage(next, source, plan.signature);
            if (page.signature !== plan.signature) throw new Error('Changed cart context');
            if (page.sort !== 'price-ascending') throw new Error('Collection price order unavailable');
            for (const candidate of page.candidates) {
              if (!candidate.handle || handles.has(candidate.handle)) continue;
              handles.add(candidate.handle);
              candidates.push(candidate);
              if (candidates.length >= 2) break;
            }
            next = page.next;
          } catch {
            failures++;
            break; // Try another related source; keep cart operations available.
          }
        }
      }
      return { candidates: candidates.slice(0, 2), failures };
    }
    connectedCallback() { queueMicrotask(() => { if (this.isConnected) this.load(); }); }
    retry() { if (['error', 'partial'].includes(this.dataset.state)) this.load(); }
    async load() {
      if (this.pending) return this.pending;
      const data = this.querySelector('[data-bt-cart-addon-plan]');
      if (!data) return;
      let plan;
      try { plan = JSON.parse(data.textContent); } catch { return; }
      const key = JSON.stringify(plan) + ':' + (window.Shopify?.theme?.id || '');
      const cached = cache.get(key);
      const request = cached && Date.now() - cached.at < 30000 ? cached.request
        : BrincatoysCartAddons.resolve(plan, this.loadPage.bind(this));
      if (!cached || request !== cached.request) {
        cache.set(key, { at: Date.now(), request });
        while (cache.size > 12) cache.delete(cache.keys().next().value);
      }
      this.dataset.state = 'loading';
      this.pending = request.then(result => {
        if (result.failures) cache.delete(key);
        if (!this.isConnected) return;
        this.render(result.candidates);
        this.dataset.state = result.failures ? (result.candidates.length ? 'partial' : 'error') : 'ready';
      }).catch(() => {
        cache.delete(key);
        if (this.isConnected) this.dataset.state = 'error';
      }).finally(() => { this.pending = null; });
      return this.pending;
    }
    async loadPage(path, source) {
      const url = new URL(path, window.location.origin);
      const base = new URL(source.url, window.location.origin);
      if (url.origin !== window.location.origin || url.pathname !== base.pathname || !url.pathname.includes('/collections/')) {
        throw new Error('Invalid collection source');
      }
      url.searchParams.set('section_id', 'brincatoys-cart-addon-candidates');
      url.searchParams.set('sort_by', 'price-ascending');
      url.searchParams.set('filter.v.availability', '1');
      url.searchParams.set('filter.v.price.lte', (source.ceiling / 100).toFixed(2));
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 6000);
      try {
        const response = await fetch(cartSectionUrl(url.pathname + url.search), { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Collection request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const root = html.querySelector('[data-bt-cart-addon-response]');
        if (!root) throw new Error('Collection section unavailable');
        return { signature: root.dataset.signature, sort: root.dataset.sort, next: root.dataset.next,
          candidates: [...root.querySelectorAll('template[data-bt-cart-addon-candidate]')].map(template => ({ handle: template.dataset.handle, template })) };
      } finally { clearTimeout(timer); }
    }
    initializeForms() {
      return customElements.whenDefined('product-form').then(() => {
        if (this.isConnected) this.querySelectorAll('product-form').forEach(form => form.init?.());
      });
    }
    render(candidates) {
      const root = this.querySelector('product-recommendations');
      if (!root) return;
      const seeds = new Map([...root.querySelectorAll('li[data-bt-cart-addon-handle]')].map(card => [card.dataset.btCartAddonHandle, card]));
      const cards = candidates.map(candidate => {
        const template = candidate.template?.content.querySelector('template[data-surface="' + this.dataset.surface + '"]');
        const card = seeds.get(candidate.handle)?.cloneNode(true) || template?.content.firstElementChild?.cloneNode(true);
        if (card) card.dataset.btCartAddonHandle = candidate.handle;
        return card;
      }).filter(Boolean);
      const slider = root.querySelector('[data-bt-cart-addon-slider]');
      if (slider) {
        const prototype = slider.firstElementChild.cloneNode(true);
        const perPage = Number(this.dataset.perPage) || 1;
        const slides = [];
        for (let start = 0; start < Math.max(cards.length, 1); start += perPage) {
          const slide = prototype.cloneNode(true);
          slide.id = 'Slide-CartAddons-' + this.dataset.surface + '-' + (slides.length + 1);
          slide.querySelector('ul').replaceChildren(...cards.slice(start, start + perPage));
          slide.setAttribute('aria-label', (slides.length + 1) + ' ' + this.dataset.slideOf + ' ' + Math.ceil(cards.length / perPage));
          slides.push(slide);
        }
        slider.replaceChildren(...slides);
        const controls = root.querySelector('[data-bt-cart-addon-controls]');
        if (controls) {
          controls.hidden = slides.length < 2;
          controls.style.display = slides.length < 2 ? 'none' : '';
          const track = controls.querySelector('.slider-scrollbar__track');
          if (track) { track.dataset.numSlides = String(slides.length); track.style.setProperty('--num-slides', slides.length); }
        }
      } else root.querySelector('[data-bt-cart-addon-cards]')?.replaceChildren(...cards);
      root.hidden = !cards.length;
      root.style.display = cards.length ? '' : 'none';
      root.classList.toggle('product-recommendations--loaded', !!cards.length);
      window.loadTemplateContent?.(this);
      this.initializeForms();
      window.loadDesktopOnlyTemplates?.();
      window.stickersReinit?.();
      const slideshow = root.querySelector('slideshow-component');
      if (slideshow?.slider) slideshow.resetPages?.();
    }
  }
  if (!customElements.get('brincatoys-cart-addons')) customElements.define('brincatoys-cart-addons', BrincatoysCartAddons);
})();
